import { createContext, useState } from 'react'
import { v4 as uuid } from 'uuid'
import useLocalStorage from 'use-local-storage'

export const GlobalContext = createContext()

/* create state variables and export context provider */
const GlobalState = props => {
  const [ server, setServer ] = useState(null)
  const [ user, setUser ] = useState(null)
  const [ lists, setLists ] = useState(null)
  const [ items, setItems ] = useState(null)
  const [ users, setUsers ] = useState([])
  const [ members, setMembers ] = useState([])
  const [ status, setStatus ] = useState('idle')
  const [ error, setError ] = useState(null)
  const [ alerts, setAlerts] = useState([])

  // localStorage data used for offline readonly functionality if enabled
  const [offlineStorage, setOfflineStorage] = useLocalStorage('offline', true);
  const [cachedUser, setCachedUser] = useLocalStorage("cachedUser", '');
  const [cachedLists, setCachedLists] = useLocalStorage("cachedLists", []);
  const [cachedItems, setCachedItems] = useLocalStorage("cachedItems", []);

  // login
  const login = async loginInfo => {
    setStatus('pending')
    try {
      const response = await fetch(`${loginInfo.server}/${loginInfo.username}`)
      if (response.ok) {
        const data = await response.json()
        setServer(loginInfo.server)
        setUser(data.data[0])
        setCachedUser(data.data[0])
        setStatus('idle')
      }
      else {
        setError("Status: " + response.status + " " + response.statusText)
        setStatus('error')
      }
    } catch(error) {
        //some sh!t happened to the network
        //if(offlineStorage && loginInfo.username === cachedUser.username) {
        if(loginInfo.username === cachedUser.username) {
          setUser(cachedUser)
          setStatus('offline')
        }
        else {
          setError("There was a network error. Data not currently available")
          setStatus('offline')
        }
    }
  }

  // get user
  const getUser = async () => {
    setStatus('pending')
    try {
      const response = await fetch(`${server}/${user.username}`)
      if (response.ok) {
        const data = await response.json()
        setUser(data.data[0])
        setStatus('idle')
      }
      else {
        setAlert(await response.statusText, "danger")
        setStatus('error')
      }
    } 
    catch(error) {
      setStatus('offline')
      setAlert("Could not get user data", "danger")
    }
  }

  // update user
  const updateUser = async updatedUser => {
    setStatus('pending')
    try {
      const options = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedUser)
      }
      const response = await fetch(`${server}/${user.username}`, options)
      if (response.ok) {
        setStatus('idle')
        setError('')
        setAlert('Profile udpated', 'success')
        getUser()
      }
      else {
        setStatus('error')
        setAlert(await response.text(), 'danger')
      }
    } 
    catch(error) {
      setStatus('offline')
      setAlert("Could not update user data", "danger")
    }
  }

  // get lists
  const getLists = async () => {
    setStatus('pending')
    try {
      const response = await fetch(`${server}/${user.username}/lists`)
      if (response.ok) {
        const data = await response.json()
        setLists(data.data)
        setCachedLists(data.data)
        setStatus('idle')
      } 
      else {
        setError(response.statusText)
        setStatus('error')
      }
    } 
    catch(error) {
      setStatus('offline')
      setAlert("Could not get user data", "danger")
    }
  }

  // add list
  const addList = async list => {
    try {
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(list)
      }
      const response = await fetch(`${server}/${user.username}/lists`, options)
      if (response.ok) {
        setStatus('idle')
        setError('')
        setAlert('Added new list!', 'success')
        getAll()
      } 
      else {
        setStatus('error')
        setAlert(await response.text(), 'danger')
      }
    } 
    catch(error) {
      setStatus('offline')
      setAlert("Could not add list", "danger")
    }
  }

  // update list
  const updateList = async list => {
    setStatus('pending')
    try {
      const options = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(list)
      }
      const response = await fetch(`${server}/${user.username}/lists/${list.listID}`, options)
      console.log('Add list api call running...')
      if (response.ok) {
        setStatus('idle')
        setError('')
        setAlert('Updated list successfully', 'success')
        getAll()
      } 
      else {
        setStatus('error')
        setAlert(await response.text(), 'danger')
      }
    } 
    catch(error) {
      setStatus('offline')
      setAlert("Could not update list", "danger")
    }
  }

  // delete list
  const deleteList = async listID => {
    setStatus('pending')
    try {
      const response = await fetch(`${server}/${user.username}/lists/${listID}`, { method: 'DELETE' })
      if (response.ok) {
        setStatus('idle')
        setError('')
        setAlert('List removed', 'success')
        getAll()
      } 
      else {
        setStatus('error')
        setAlert(await response.text(), 'danger')
      }
    } 
    catch(error) {
      setStatus('offline')
      setAlert("Could not delete list", "danger")
    }
  }

  // get items
  const getItems = async () => {
    setStatus('pending')
    try {
      const response = fetch(`${server}/${user.username}/items`)
      if(response.ok) {
        const data = await response.json()
        setItems(data.data)
        setCachedItems(data.data)
        setStatus('idle')
      } 
      else {
        setError(response.statusText)
        setStatus('error')
      }
    } catch(error) {
        setStatus('offline')
        setAlert("Could not get items", "danger")
      }
  }

  // add item
  const addItem = async item => {
    setStatus('pending')
    try {
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
      }
      const response = await fetch(`${server}/${user.username}/lists/${item.listID}`, options)
      if (response.ok) {
        setStatus('idle')
        setError('')
        setAlert('Added new item!', 'success')
        getAll()
      } else {
        setStatus('error')
        setAlert(await response.text(), 'danger')
      }
    } catch(error) {
      setStatus('offline')
      setAlert("Could not add item", "danger")
    }
  }

  // update an item
  const updateItem = async item => {
    setStatus('pending')
    try {
      const options = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
      }
      const response = await fetch(`${server}/${user.username}/lists/${item.listID}/${item.itemID}`, options)
      if (response.ok) {
        setStatus('idle')
        setError('')
        setAlert('Update successful', 'success')
        getAll()
        console.log('updateItems from Context Successful')
      } else {
        //setError(await response.text())
        setStatus('error')
        setAlert(await response.text(), 'danger')
      }
    } catch(error) {
      setStatus('offline')
      setAlert("Could not update item", "danger")
    }
  }

  // delete an item
  const deleteItem = async item => {
    setStatus('pending')
  try {
    const response = await fetch(`${server}/${user.username}/lists/${item.listID}/${item.itemID}`, { method: 'DELETE' })
    if (response.ok) {
      setStatus('idle')
      setError('')
      setAlert('Item successfully removed', 'success')
      getAll()
    } else {
      setStatus('error')
      setAlert(await response.text(), 'danger')
    }
  } catch(error) {
    setStatus('offline')
    setAlert("Could not delete item", "danger")
  }
  }

  // get all user data
  const getAll = async () => {
    setStatus('pending')
    try {  
      const response = await fetch(`${server}/${user.username}/all`)
      if (response.ok) {
        const data = await response.json()
        setLists(data.lists)
        setCachedLists(data.lists)
        setItems(data.items)
        setCachedItems(data.items)
        setStatus('idle')
      }
      else {
        setError(response.statusText)
        setStatus('error')
      }
    } catch(error) {
      //some sh!t happened to the network
      if(offlineStorage) {
        setLists(cachedLists)
        setItems(cachedItems)
      }
      setStatus('offline')
    }
  }

  // get all users
  const getAllUsers = async () => {
    setStatus('pending')
    try {
      const response = await fetch(`${server}/${user.username}/users`)
      if (response.ok) {
        const data = await response.json()
        setUsers(data.data)
        setStatus('idle')
      }
      else {
        setError(response.statusText)
        setStatus('error')
      }
    } catch(error) {
      //some sh!t happened to the network
      setError("There was a network error. Data not currently available")
      setStatus('offline')
    }
  }

  // add friend
  const addFriend = async friendID => {
    setStatus('pending')
    try {
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: 'none'
      }
      const response = await fetch(`${server}/${user.userID}/friend/${friendID}`, options)
      if (response.ok) {
        getAllUsers()
        setStatus('idle')
      } else {
        setError(await response.text())
        setStatus('error')
      }
    } catch(error) {
        setStatus('offline')
        setAlert("Could not add friend", "danger")
    }
  }

  // remove friend
  const removeFriend = async friendID => {
    setStatus('pending')
    try {
      const response = await fetch(`${server}/${user.userID}/friend/${friendID}`, { method: 'DELETE' })
      if (response.ok) {
        getAllUsers()
        setStatus('idle')
      } else {
        setError(await response.text())
        setStatus('error')
      }
    } catch(error) {
        setStatus('offline')
        setAlert("Could not remove friend", "danger")
    }
  }

  // Get all shared members for list
  const getMembers = async listID => {
    try {
      setStatus('pending')
      const response = await fetch(`${server}/${user.username}/lists/${listID}/members`)
      if(response.ok) {
        const data = await response.json()
        setMembers(data.data.filter(member => member.username !== user.username))
        setStatus('idle')
      }
      else {
        setStatus('error')
        setAlert(await response.text(), 'danger')
      }
    } catch(error) {
      //some sh!t happened to the network
      setStatus('offline')
    }
  }

  // add new member to share this list
  const addMember = async (shareUser, listID)=> {
    try {
      const options = {
        method: 'PUT',
        headers: { 'Content-Type': 'text/plain' },
        body: shareUser.username
      }
      const response = await fetch(`${server}/${user.username}/lists/${listID}/members`, options)
      if (response.ok) {
        setStatus('idle')
        setError('')
        setAlert("List is now shared with " + shareUser.username, "success")
        getMembers(listID)
      } 
      else {
        setStatus('error')
        setAlert(await response.text(), 'danger')
      }
    } catch(error) {
        setStatus('offline')
        setAlert("Error attempting to share", "danger")
    }
  }

  // remove user from shared members list
  const removeMember = async (removeUser, listID) => {
    try {
      const options = {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(removeUser)
      }
      const response = await fetch(`${server}/${user.username}/lists/${listID}/members`, options)
      if (response.ok) {
        setStatus('idle')
        setError('')
        setAlert(removeUser.username + " has been removed from list", "warning")
        getMembers(listID)
      } 
      else {
        setStatus('error')
        setAlert(await response.text(), 'danger')
      }
    } catch(error) {
        setStatus('offline')
        setAlert("Error attempting to unshare", "danger")
    }
  }

  // Set Alert
  const setAlert = (msg, type, timeout = 3000) => {
    const id = uuid()
    setAlerts([...alerts, { msg, type, id }])
    setTimeout(() => setAlerts(alerts => [...alerts.filter(alert => alert.id !== id)]), timeout)
  }

  return (
    <GlobalContext.Provider value={{ server, user, lists, items, users, members, error, status, alerts, login, getUser, updateUser, getLists, addList, updateList, deleteList, getItems, addItem, updateItem, deleteItem, getAll, getAllUsers, addFriend, removeFriend, getMembers, addMember, removeMember, setError, setStatus }}>
      { props.children }
    </GlobalContext.Provider>
  )
}

export default GlobalState